import store from '@/store'
import * as moment from 'moment'
import * as _ from 'lodash'

/**
 * 管理システムから遷移してきた場合を判定
 * （フロントエンドログイン、書籍プレビューのみ。
 * アカウントログインは通常ユーザーの処理として扱うので対象外。）
 */
export const isFromAdminSystem = () => {
  return (
    store.state.profile.login_type === 2 || // フロントエンドログイン
    store.state.profile.login_type === 7 // 書籍プレビュー
  )
}

/**
 * PCかSPか判定する
 */
export const judgeDevice = () => {
  const ua = navigator.userAgent
  if (
    ua.indexOf('iPhone') >= 0 || 
    ua.indexOf('iPad') >= 0 || // iPad(iOS12以前)
    (ua.indexOf('Macintosh') >= 0 && 'ontouchend' in document) || // iPad OS(iOS13以降)
    ua.indexOf('iPod') >= 0 || 
    // Androidスマホのみなら、Android と Mobileで判定する必要があるが、
    // Androidタブレットも含めるため、Androidのみで判定する。
    // その場合、Oculus Go、Amazon Fire TV も条件にあてはまるが、
    // LCがこれらの端末で見ることは無いという想定とする。
    ua.indexOf('Android') >= 0
  ) {
    // iPhone、iPad、Androidの場合
    return 'sp'
  } else {
    // PC、それ以外の場合
    return 'pc'
  }
}

/**
 * デバイスがSPかどうか
 * 
 * @return {boolean}
 */
export const isSp = () => {
  return judgeDevice() === 'sp'
}

/**
 * デバイスがPCかどうか
 *
 * @return {boolean}
 */
export const isPc = () => {
  return judgeDevice() === 'pc'
}

/**
 * デバイスがiOSか
 * @return {boolean}
 */
export const isIos = () => {
  const ua = navigator.userAgent
  return ua.indexOf('iPhone') >= 0 ||
    ua.indexOf('iPad') >= 0 || // iPad(iOS12以前)
    (ua.indexOf('Macintosh') >= 0 && 'ontouchend' in document) || // iPad OS(iOS13以降)
    ua.indexOf('iPod') >= 0
}

/**
 * デバイスがiPadか
 * @return {boolean}
 */
export const isIPad = () => {
  const ua = navigator.userAgent
  return ua.indexOf('iPad') >= 0 || // iPad(iOS12以前)
  (ua.indexOf('Macintosh') >= 0 && 'ontouchend' in document) // iPad OS(iOS13以降)
}

/**
 * デバイス判定をし、PDF表示用の適切な設定値を取得する
 * 
 */
export const getSettingValuesForPDF = () => {
  
  let PIXEL_RATIO = 1
  let VIEWPORT_RATIO = 1
  let DEFAULT_VIEWPORT_SCALE = 1

  if (isIPad()) {
    // iPadの場合
    // メモリ制限で表示されなくなるため、解像度を下げる
    PIXEL_RATIO = window.devicePixelRatio * 1.5 || 1 * 1.5
    VIEWPORT_RATIO = 0.98
    DEFAULT_VIEWPORT_SCALE = 1.80
  } else {
    // それ以外
    PIXEL_RATIO = window.devicePixelRatio * 3 || 1 * 3
    VIEWPORT_RATIO = 0.98
    DEFAULT_VIEWPORT_SCALE = 1.10
  }

  return { PIXEL_RATIO, VIEWPORT_RATIO, DEFAULT_VIEWPORT_SCALE }
}

/**
 * URLからクエリ文字列の値を取得する
 * @param {string} key [任意] パラメータ名
 * @return keyが指定されている場合はkeyに対する値、
 *         指定されていない場合は全てのクエリ文字列のキーと値が設定されたオブジェクト 
 */
export const getQueryParam = (key) => {
  const url = window.location.search
  const map = {}

  const hash = url.slice(1).split('&')
  for (let i = 0; i < hash.length; i++) {
    const array = hash[i].split('=')
    map[array[0]] = array[1]
  }

  if (key) {
    return map[key] ?? ''
  } else {
    return map
  } 
}

/**
 * 郵便番号ハイフン付与
 * @param  {string} 7文字の文字列
 * 
 * @return {string} 変換に成功した場合はxxx-xxxx。
 */
export const addHyphenForZipcode = (string) => {
  const regExp = /([0-9]{3})([0-9]{4})/
  return regExp.test(string)
    ? string.replace(regExp, '$1-$2')
    : string
}

/**
 * 日付形式変換
 * @param  {string} 日付形式の文字列
 * 
 * @return {string} YYYY/MM/DDの形式にした文字列
 */
export const formatDate = (string) => {
  return moment(string).isValid() ? moment(string).format('YYYY/MM/DD') : string
}

/**
 * 日付形式変換2
 * @param  {string} 日付形式の文字列
 *
 * @return {string} YYYY年M月D日の形式にした文字列
 */
export const formatDateKanji = (string) => {
  return moment(string).isValid() ? moment(string).format('YYYY年M月D日') : string
}

/**
 * 日付の差分
 * @param  {string} date1, 日付形式の文字列
 * @param  {string} date2, 日付形式の文字列
 *
 * @return {string} 1ヶ月未満の場合、0日
 *                  それ以上の場合は、○ヶ月
 */
export const dateDiff = (date1, date2) => {
  const a = date1 === '本日'
    ? moment()
    : moment(date1)
  const b = date2 === '本日'
    ? moment()
    : moment(date2)
  if (!a.isValid() || !b.isValid()) {
    return ''
  }

  // 大きい日付に1日加算
  const left = b.isAfter(a) ? a : b
  const right = b.isAfter(a) ? b.add(1, 'days') : a.add(1, 'days')

  // const yearDiff = left.diff(right, 'years')
  const monthDiff = Math.abs(left.diff(right, 'months'))
  const dayDiff = Math.abs(left.diff(right, 'days'))

  if (monthDiff > 0) {
    return monthDiff + 'ヶ月'
  } else {
    return dayDiff + '日'
  }
}

/**
 * 申込Noの加工
 * @param  {string} string, 申込No(96 + MMDD + 4桁連番)
 *
 * @return {string} 形式が誤っている場合、「発番中」の文言
 */
export const applicationNoIssuing = (string) => {
  const regExp = /(96)(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])([0-9]{5,})/
  return regExp.test(string)
    ? '発番中'
    : string
}

/**
 * 指定した元号に対する最終年を取得
 */
export const getLastYearOfEra = (era) => {
  let lastYear = 0
  switch (era) {
    case '明治':
      lastYear = 45
      break
    case '大正':
      lastYear = 15
      break
    case '昭和':
      lastYear = 64
      break
    case '平成':
      lastYear = 31
      break
    case '令和':
      lastYear = (new Date()).getFullYear() - 2018
      break
  }
  return lastYear
}

/**
 * 元号の年から西暦の年に変換する
 */
export const convertElaYearToGregorianYear = (era, year) => {
  let gregorianYear = 0
  switch (era) {
    case '明治':
      gregorianYear = year + 1867
      break
    case '大正':
      gregorianYear = year + 1911
      break
    case '昭和':
      gregorianYear = year + 1925
      break
    case '平成':
      gregorianYear = year + 1988
      break
    case '令和':
      gregorianYear = year + 2018
      break
  }
  return gregorianYear
}

/**
 * 西暦から和暦に変換する
 */
export const convertToJapaneseEra = (dateString, type = 'string') => {
  const date = new Date(dateString)

  const eraNames = [
    { name: '令和', start: new Date('2019-05-01') },
    { name: '平成', start: new Date('1989-01-08') },
    { name: '昭和', start: new Date('1926-12-25') },
    { name: '大正', start: new Date('1912-07-30') },
    { name: '明治', start: new Date('1868-01-25') },
    { name: '明治', start: new Date('1868-01-01') } // 明治より前の入力値はないが、エラーになるので明治で返す
  ]

  for (const era of eraNames) {
    if (date >= era.start) {
      let japaneseYear = date.getFullYear() - era.start.getFullYear() + 1
      if (japaneseYear === 1) japaneseYear = '元'

      const japaneseMonth = date.getMonth() + 1
      const japaneseDay = date.getDate()
      if (type === 'string') {
        return `${era.name}${japaneseYear}年${japaneseMonth}月${japaneseDay}日`
      } else if (type === 'array') {
        const returnArray = {
          era: era.name,
          year: japaneseYear,
          month: japaneseMonth,
          day: japaneseDay
        }
        return returnArray
      }
    }
  }

  return dateString
}

/**
 * apicommon.fetchFileから取得したデータをdataurlに変換
 * @param  {object} apicommon.fetchFileから返却されたレスポンス
 * 
 * @return {promise} dataURL
 */
export const getDataURL = (res) => {
  return new Promise((resolve, reject) => {
    const contentType = res.headers['content-type']
    const blob = new Blob([res.data], { type: contentType })
    const reader = new FileReader()
    reader.readAsDataURL(blob)
    reader.onload = (ev) => {
      resolve(ev.target.result)
    }
    reader.onerror = (ev) => reject(ev) 
  })
}

// ASCII文字のマップ(半角→全角)
const asciiMapH2Z = {
  '!': '！',
  '"': '”',
  '#': '＃',
  '$': '＄',
  '%': '％',
  '&': '＆',
  '\'': '’',
  '(': '（',
  ')': '）',
  '*': '＊',
  '+': '＋',
  ',': '，',
  '-': '－',
  '.': '．',
  '/': '／',
  '0': '０',
  '1': '１',
  '2': '２',
  '3': '３',
  '4': '４',
  '5': '５',
  '6': '６',
  '7': '７',
  '8': '８',
  '9': '９',
  ':': '：',
  ';': '；',
  '<': '＜',
  '=': '＝',
  '>': '＞',
  '?': '？',
  '@': '＠',
  'A': 'Ａ',
  'B': 'Ｂ',
  'C': 'Ｃ',
  'D': 'Ｄ',
  'E': 'Ｅ',
  'F': 'Ｆ',
  'G': 'Ｇ',
  'H': 'Ｈ',
  'I': 'Ｉ',
  'J': 'Ｊ',
  'K': 'Ｋ',
  'L': 'Ｌ',
  'M': 'Ｍ',
  'N': 'Ｎ',
  'O': 'Ｏ',
  'P': 'Ｐ',
  'Q': 'Ｑ',
  'R': 'Ｒ',
  'S': 'Ｓ',
  'T': 'Ｔ',
  'U': 'Ｕ',
  'V': 'Ｖ',
  'W': 'Ｗ',
  'X': 'Ｘ',
  'Y': 'Ｙ',
  'Z': 'Ｚ',
  '[': '［',
  '\\': '￥',
  ']': '］',
  '^': '＾',
  '_': '＿',
  '`': '｀',
  'a': 'ａ',
  'b': 'ｂ',
  'c': 'ｃ',
  'd': 'ｄ',
  'e': 'ｅ',
  'f': 'ｆ',
  'g': 'ｇ',
  'h': 'ｈ',
  'i': 'ｉ',
  'j': 'ｊ',
  'k': 'ｋ',
  'l': 'ｌ',
  'm': 'ｍ',
  'n': 'ｎ',
  'o': 'ｏ',
  'p': 'ｐ',
  'q': 'ｑ',
  'r': 'ｒ',
  's': 'ｓ',
  't': 'ｔ',
  'u': 'ｕ',
  'v': 'ｖ',
  'w': 'ｗ',
  'x': 'ｘ',
  'y': 'ｙ',
  'z': 'ｚ',
  '{': '｛',
  '|': '｜',
  '}': '｝',
  '~': '～'
}

// ASCII文字のマップ(全角→半角)
const asciiMapZ2H = {
  '！': '!',
  '”': '"',
  '＃': '#',
  '＄': '$',
  '％': '%',
  '＆': '&',
  '’': '\'',
  '（': '(',
  '）': ')',
  '＊': '*',
  '＋': '+',
  '，': ',',
  '－': '-',
  '．': '.',
  '／': '/',
  '０': '0',
  '１': '1',
  '２': '2',
  '３': '3',
  '４': '4',
  '５': '5',
  '６': '6',
  '７': '7',
  '８': '8',
  '９': '9',
  '：': ':',
  '；': ';',
  '＜': '<',
  '＝': '=',
  '＞': '>',
  '？': '?',
  '＠': '@',
  'Ａ': 'A',
  'Ｂ': 'B',
  'Ｃ': 'C',
  'Ｄ': 'D',
  'Ｅ': 'E',
  'Ｆ': 'F',
  'Ｇ': 'G',
  'Ｈ': 'H',
  'Ｉ': 'I',
  'Ｊ': 'J',
  'Ｋ': 'K',
  'Ｌ': 'L',
  'Ｍ': 'M',
  'Ｎ': 'N',
  'Ｏ': 'O',
  'Ｐ': 'P',
  'Ｑ': 'Q',
  'Ｒ': 'R',
  'Ｓ': 'S',
  'Ｔ': 'T',
  'Ｕ': 'U',
  'Ｖ': 'V',
  'Ｗ': 'W',
  'Ｘ': 'X',
  'Ｙ': 'Y',
  'Ｚ': 'Z',
  '［': '[',
  '￥': '\\',
  '］': ']',
  '＾': '^',
  '＿': '_',
  '｀': '`',
  'ａ': 'a',
  'ｂ': 'b',
  'ｃ': 'c',
  'ｄ': 'd',
  'ｅ': 'e',
  'ｆ': 'f',
  'ｇ': 'g',
  'ｈ': 'h',
  'ｉ': 'i',
  'ｊ': 'j',
  'ｋ': 'k',
  'ｌ': 'l',
  'ｍ': 'm',
  'ｎ': 'n',
  'ｏ': 'o',
  'ｐ': 'p',
  'ｑ': 'q',
  'ｒ': 'r',
  'ｓ': 's',
  'ｔ': 't',
  'ｕ': 'u',
  'ｖ': 'v',
  'ｗ': 'w',
  'ｘ': 'x',
  'ｙ': 'y',
  'ｚ': 'z',
  '｛': '{',
  '｜': '|',
  '｝': '}',
  '～': '~' 
}

/**
 * ASCII文字の半角を全角に変換する
 * @param {} string 
 * @return 変換後の文字列
 */
export const convertAsciiH2Z = (string) => {
  let newString = ''
  for (let i = 0; i < string.length; i++) {
    const ch = string.charAt(i)
    newString += asciiMapH2Z[ch] ?? ch
  }
  return newString
}

/**
 * ASCII文字の全角を半角に変換する
 * @param {} string 
 * @return 変換後の文字列
 */
export const convertAsciiZ2H = (string) => {
  let newString = ''
  for (let i = 0; i < string.length; i++) {
    const ch = string.charAt(i)
    newString += asciiMapZ2H[ch] ?? ch
  }
  return newString
}

// 半角カタカナのマップ
const hankakuKatakanaMap = {
  'ｶﾞ': 'ガ', 
  'ｷﾞ': 'ギ', 
  'ｸﾞ': 'グ',
  'ｹﾞ': 'ゲ', 
  'ｺﾞ': 'ゴ',
  'ｻﾞ': 'ザ', 
  'ｼﾞ': 'ジ',
  'ｽﾞ': 'ズ',
  'ｾﾞ': 'ゼ',
  'ｿﾞ': 'ゾ',
  'ﾀﾞ': 'ダ',
  'ﾁﾞ': 'ヂ', 
  'ﾂﾞ': 'ヅ', 
  'ﾃﾞ': 'デ', 
  'ﾄﾞ': 'ド',
  'ﾊﾞ': 'バ',
  'ﾋﾞ': 'ビ',
  'ﾌﾞ': 'ブ', 
  'ﾍﾞ': 'ベ', 
  'ﾎﾞ': 'ボ',
  'ﾊﾟ': 'パ', 
  'ﾋﾟ': 'ピ', 
  'ﾌﾟ': 'プ', 
  'ﾍﾟ': 'ペ',
  'ﾎﾟ': 'ポ',
  'ｳﾞ': 'ヴ',
  'ﾜﾞ': 'ヷ',
  'ｦﾞ': 'ヺ',
  'ｱ': 'ア',
  'ｲ': 'イ',
  'ｳ': 'ウ', 
  'ｴ': 'エ',
  'ｵ': 'オ',
  'ｶ': 'カ',
  'ｷ': 'キ',
  'ｸ': 'ク', 
  'ｹ': 'ケ', 
  'ｺ': 'コ',
  'ｻ': 'サ',
  'ｼ': 'シ',
  'ｽ': 'ス',
  'ｾ': 'セ',
  'ｿ': 'ソ',
  'ﾀ': 'タ',
  'ﾁ': 'チ',
  'ﾂ': 'ツ',
  'ﾃ': 'テ',
  'ﾄ': 'ト',
  'ﾅ': 'ナ',
  'ﾆ': 'ニ',
  'ﾇ': 'ヌ',
  'ﾈ': 'ネ',
  'ﾉ': 'ノ',
  'ﾊ': 'ハ',
  'ﾋ': 'ヒ',
  'ﾌ': 'フ',
  'ﾍ': 'ヘ',
  'ﾎ': 'ホ',
  'ﾏ': 'マ',
  'ﾐ': 'ミ',
  'ﾑ': 'ム',
  'ﾒ': 'メ',
  'ﾓ': 'モ',
  'ﾔ': 'ヤ',
  'ﾕ': 'ユ',
  'ﾖ': 'ヨ',
  'ﾗ': 'ラ',
  'ﾘ': 'リ',
  'ﾙ': 'ル',
  'ﾚ': 'レ',
  'ﾛ': 'ロ',
  'ﾜ': 'ワ',
  'ｦ': 'ヲ',
  'ﾝ': 'ン',
  'ｧ': 'ァ',
  'ｨ': 'ィ',
  'ｩ': 'ゥ',
  'ｪ': 'ェ',
  'ｫ': 'ォ',
  'ｯ': 'ッ',
  'ｬ': 'ャ',
  'ｭ': 'ュ',
  'ｮ': 'ョ',
  '｡': '。',
  '､': '、',
  'ｰ': 'ー',
  '｢': '「',
  '｣': '」',
  '･': '・'
}

/**
 * 半角カタカナを全角カタカナに変換する
 * @param {} string 
 * @return 変換後の文字列
 */
export const convertKatakanaH2Z = (string) => {
  const regex = new RegExp('(' + Object.keys(hankakuKatakanaMap).join('|') + ')', 'g')
  return string
    .replace(regex, function (match) {
      return hankakuKatakanaMap[match]
    })
    .replace(/ﾞ/g, '゛')
    .replace(/ﾟ/g, '゜')
}

/**
 * リストデータの全要素にユニークなID(数値)を付与する
 * 用途：ユニークIDを一括で付与したい場合に使用。
 * （※APIが全ノードにユニークなIDを付与している場合は、そのIDを使用し、このメソッドは使用しないでください。）
 * @param {Array<object>} data [INPUT/OUTPUT] オブジェクトのリスト(参照渡し)
 * @param {string} keyForId ユニークIDのオブジェクトのキー名
 * @param {object} __param [INPUT/OUTPUT] 付与するIDを管理するオブジェクト
 *                         (内部処理専用。本メソッドを呼び出す場合は記述不要) 
 *                         (JSはプリミティブ型の参照渡しができないためオブジェクト型にしている)
 */
export const addUniqueIdForList = (data, keyForId, __param = null) => {
  if (!__param) {
    __param = {}
    __param.id = 0
  }

  for (let k = 0; k < data.length; k++) {
    const key = data[k][keyForId] = __param.id
    __param.id++
  }
}

/**
 * ツリーデータの全ノードにユニークなID(数値)を付与する
 * 用途：ユニークIDを一括で付与したい場合に使用。
 * （※APIが全ノードにユニークなIDを付与している場合は、そのIDを使用し、このメソッドは使用しないでください。）
 * @param {object} node [INPUT/OUTPUT] ツリーデータのノード(参照渡し)
 * @param {string} keyForId ユニークIDのオブジェクトのキー名
 * @param {string or Array<string>} keysForChildren 子ノードリストのオブジェクトのキー名
 * @param {object} __param [INPUT/OUTPUT] 付与するIDを管理するオブジェクト
 *                         (内部処理専用。本メソッドを呼び出す場合は記述不要) 
 *                         (JSはプリミティブ型の参照渡しができないためオブジェクト型にしている)
 */
export const addUniqueIdForTreeData = (node, keyForId, keysForChildren, __param = null) => {

  if (!Array.isArray(keysForChildren)) {
    keysForChildren = [keysForChildren]
  }

  if (!__param) {
    __param = {}
    __param.id = 0
  }

  node[keyForId] = __param.id
  __param.id++

  for (let k = 0; k < keysForChildren.length; k++) {
    const key = keysForChildren[k]
    if (typeof node[key] !== 'undefined' && Array.isArray(node[key])) {
      for (let i = 0; i < node[key].length; i++) {
        const childData = node[key][i]
        addUniqueIdForTreeData(childData, keyForId, keysForChildren, __param)
      }
    }  
  }
}

/**
 * 数値を3桁区切りに変換
 * @param  {string, number} 変換元の文字列or数値
 *
 * @return {string} 3桁区切りの形式にした文字列
 */
export const breakComma = (number) => number ? number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '0'

/**
 * HTMLエスケープ
 * @param {string} 文字列
 * @return エスケープした文字列 
 */
export const escapeHTML = (string) => {
  string = string.replace(/&/g, '&amp;')
  string = string.replace(/</g, '&lt;')
  string = string.replace(/>/g, '&gt;')
  string = string.replace(/"/g, '&quot;')
  return string
}

/**
 * HTMLエスケープしてから<br>付与
 * @param {string} 文字列
 * @return エスケープした文字列 
 */
export const escapeHTMLAndBr = (string) => {
  string = escapeHTML(string)
  string = string.replace(/\r?\n/g, '<br>')
  return string
}

/**
 * 背景画像を取得
 */
export const getTemplateImage = (template) => {
  if (template === 1) {
    return require('@/assets/images/bg_1.jpg')
  } else if (template === 2) {
    return require('@/assets/images/bg_2.jpg')
  } else if (template === 3) {
    return require('@/assets/images/bg_3.jpg')
  } else if (template === 4) {
    return require('@/assets/images/bg_4.jpg')
  } else if (template === 5) {
    return require('@/assets/images/bg_5.jpg')
  } else if (template === 6) {
    return require('@/assets/images/bg_6.jpg')
  } else if (template === 7) {
    return require('@/assets/images/bg_7.jpg')
  } else if (template === 8) {
    return require('@/assets/images/bg_8.jpg')
  } else if (template === 9) {
    return require('@/assets/images/bg_9.jpg')
  } else {
    // 背景画像を設定してない場合（デフォルト背景）
    return require('@/assets/images/bg_4.jpg')
  }
}

/**
 * ログイン前ページ（public）用の背景画像を取得
 */
export const getPublicTemplateImage = () => {
  return require('@/assets/images/bg_4.jpg')
}

/**
 * 背景画像のリストを取得
 */
export const getTemplateImageList = () => {
  return [
    {
      template: 4,
      image: require('@/assets/images/bg_4.jpg')
    },
    {
      template: 1,
      image: require('@/assets/images/bg_1.jpg')
    },
    {
      template: 2,
      image: require('@/assets/images/bg_2.jpg')
    },
    {
      template: 3,
      image: require('@/assets/images/bg_3.jpg')
    },
    {
      template: 5,
      image: require('@/assets/images/bg_5.jpg')
    },
    {
      template: 6,
      image: require('@/assets/images/bg_6.jpg')
    },
    {
      template: 7,
      image: require('@/assets/images/bg_7.jpg')
    },
    {
      template: 8,
      image: require('@/assets/images/bg_8.jpg')
    },
    {
      template: 9,
      image: require('@/assets/images/bg_9.jpg')
    }
  ]
}


/**
 * ページタイプ(pc/sp/public)の判定
 */
export const judgePageType = () => {

  const uri = location.pathname

  let pageType = ''
  if (uri.indexOf('/pc/') >= 0) {
    pageType = 'pc'
  } else if (uri.indexOf('/sp/') >= 0) {
    pageType = 'sp'
  } else if (uri.indexOf('/public/') >= 0) {
    pageType = 'public'
  } else {
    pageType = ''
  }

  return pageType
}

/**
 * 画面の最上部にスクロールする
 */
export const scrollAreaTop = (target) => {
  if (document.querySelector(target)) document.querySelector(target).scrollTop = 0
}

/**
 * 多次元配列の特定の値を再帰的に取得する
 * {
 *  [
 *   targetKey: 1,
 *   hierarKey: [
 *     targetKey: 2,
 *     hierarKey: [
 *       targetKey: 3,
 *     ]
 *   ]
 *  ]
 * }
 * @param Object array 
 * @param string targetKey 
 * @param string hierarKey
 * 
 * @return array [1, 2, 3]
 */
export const getValuesfromNest = (array, targetKey, hierarKey) => {
  const ids = []

  const getChildrenId = (array, targetKey, hierarKey) => {
    _.forEach(array, data => {
      ids.push(data[targetKey])
      if (data[hierarKey]) {
        return getChildrenId(data[hierarKey], targetKey, hierarKey)
      }
    })
  }
  getChildrenId(array, targetKey, hierarKey)

  return ids
}

/**
 * ノートを編集したかどうかチェックする
 * @return boolean
 */
export const noteChanged = () => {
  const { id, edited, mode, forceMove } = store.getters['note/change']
  if (!forceMove && mode !== 'create' && edited) {
    return true
  } else if (!forceMove && mode === 'create') {
    return true
  }
  return false
}

/**
 * APIレスポンスの中身を走査し、エラーメッセージを表示する
 * @param {object} error API response
 */
export const commonErrorMessage = (error) => {
  if (isFromAdminSystem()) {
    // フロントエンドログイン、書籍プレビューの場合
    try {
      if (error.config && error.config.data) {
        const map = JSON.parse(error.config.data)
        const path = map._path
        if (
          path.match(/^AL\//) || // アラート系
          path.match(/^BH\//) || // 閲覧履歴系
          path.match(/^BK\//) || // ブックマーク・後で見る系
          path.match(/^ML\//) || // マイリンク系
          path.match(/^NT\//) || // ノート系
          path.match(/^TG\//) // タグ系
        ) {
          // 条件にマッチした場合はエラー表示しない
          return
        }
      }
    } catch (e) {
    }
  }

  const { data } = error
  dataErrorMessage(data)
}

export const dataErrorMessage = (data) => {
  const {
    status,
    error_message: errMsg,
    error_messages: errMsgs,
    validation_message: validateMsgs
  } = data

  const errAry = []
  errMsg && errAry.push(errMsg)

  if (errMsgs) {
    _.forEach(errMsgs, (err) => errAry.push(err))
  }

  if (validateMsgs) {
    _.forEach(validateMsgs, (err) => errAry.push(err))
  }

  const err = _.flattenDeep(errAry)

  const message = _.reduce(_.uniq(err), (sum, current) => {
    return sum
      ? sum + '\n' + current
      : current
  })

  store.dispatch('common/showNotice', { title: 'エラー', text: message })
}

/**
 * 配列でない場合は配列に変換する
 * (例) [1,2] の場合は[1,2] のまま返し、2の場合は[2]に変換して返す。
 */
export const toArray = (value) => {
  if (Array.isArray(value)) {
    return value
  } else {
    return [value]
  }
}

/*
 * WLJ APIから受信したHTMLの中から不要なタグを除外する
 * (<body>タグの内部のみを抽出する)
 */
export const removeNeedlessTagsForWLJP = (html) => {
  let newHTML = ''
  if (html) {
    newHTML = html
    newHTML = newHTML.replace(/^.*?<body.*?>/i, '')
    newHTML = newHTML.replace(/<\/body>.*$/i, '')
  }
  return newHTML
}
